import { Component, DestroyRef, inject, input, OnInit, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
	MatCell,
	MatCellDef,
	MatColumnDef,
	MatHeaderCell,
	MatHeaderCellDef,
	MatHeaderRow,
	MatHeaderRowDef,
	MatNoDataRow,
	MatRow,
	MatRowDef,
	MatTable,
	MatTableDataSource,
} from '@angular/material/table';
import { MatIcon } from '@angular/material/icon';
import { filter, Observable, Subject, switchMap, zip } from 'rxjs';
import {
	TenantPayment,
	TenantPaymentService,
	TenantSelection,
	TenantSelectionService,
	TenantSetting,
	TenantSettingService,
	TenantShare,
	TenantShareService,
	TenantTarif,
	TenantTarifService,
	UserRole,
	UserRoleService,
} from '@eadsysteme/billingclient-v1';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { MatDialog } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ParameterDialogComponent } from './parameter-dialog/parameter-dialog.component';
import { fadeInUp400ms } from '../../../../../../@vex/animations/fade-in-up.animation';
import { stagger40ms } from '../../../../../../@vex/animations/stagger.animation';
import { DateUtil } from '../../../../../util/DateUtil';
import { Icons } from '../../../../../util/Icons';
import { SessionService } from '../../../../../services/session.service';
import { ConfirmationDialogComponent } from '../../../../confirmation-dialog/confirmation-dialog.component';
import { SnackbarService } from '../../../../../services/snackbar.service';

@Component({
	selector: 'vex-parameters',
	imports: [
		MatButton,
		MatCell,
		MatCellDef,
		MatColumnDef,
		MatHeaderCell,
		MatHeaderRow,
		MatHeaderRowDef,
		MatIcon,
		MatRow,
		MatRowDef,
		MatTable,
		TranslocoDirective,
		MatHeaderCellDef,
		MatNoDataRow,
		MatIconButton,
	],
	animations: [fadeInUp400ms, stagger40ms],
	templateUrl: './parameters.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './parameters.component.scss',
})
export class ParametersComponent implements OnInit {
	protected dateUtil = inject(DateUtil);
	protected icons = inject(Icons);
	private readonly dialog = inject(MatDialog);
	private readonly sessionService = inject(SessionService);
	private readonly transloco = inject(TranslocoService);
	private readonly tenantPaymentService = inject(TenantPaymentService);
	private readonly tenantShareService = inject(TenantShareService);
	private readonly tenantTariffService = inject(TenantTarifService);
	private readonly tenantSelectionService = inject(TenantSelectionService);
	private readonly tenantSettingService = inject(TenantSettingService);

	private destroyRef = inject(DestroyRef);
	private userRoleService = inject(UserRoleService);
	private snackbar = inject(SnackbarService);
	private translocoService = inject(TranslocoService);

	billingId = input<string>();
	availableUserRoles: WritableSignal<UserRole[]> = signal([]);

	searchFilterSubject: Subject<string> = new Subject<string>();
	searchFilter$: Observable<string> = this.searchFilterSubject.asObservable();

	protected parameterConfigs = [
		{
			name: 'share',
			dataSource: null as MatTableDataSource<TenantShare>,
			columns: ['name', 'printName', 'description', 'validFrom', 'validTo', 'type', 'unit', 'options'],
		},
		{
			name: 'payment',
			dataSource: null as MatTableDataSource<TenantPayment>,
			columns: ['name', 'printName', 'description', 'validFrom', 'validTo', 'periodicity', 'type', 'options'],
		},
		{
			name: 'selection',
			dataSource: null as MatTableDataSource<TenantSelection>,
			columns: ['name', 'printName', 'description', 'validFrom', 'validTo', 'options'],
		},
		{
			name: 'tariff',
			dataSource: null as MatTableDataSource<TenantTarif>,
			columns: ['name', 'printName', 'description', 'validFrom', 'validTo', 'type', 'periodicity', 'options'],
		},
		{
			name: 'setting',
			dataSource: null as MatTableDataSource<TenantSetting>,
			columns: ['name', 'printName', 'description', 'validFrom', 'validTo', 'type', 'validUserRole', 'options'],
		},
	];

	ngOnInit(): void {
		this.loadAvailableUserRoles();
		this.fillTables();
	}

	private fillTables() {
		zip(
			this.tenantSelectionService.readAllTenantSelection(this.sessionService.effectiveArea, this.billingId()),
			this.tenantPaymentService.readAllTenantPayment(this.sessionService.effectiveArea, this.billingId()),
			this.tenantShareService.readAllTenantShare(this.sessionService.effectiveArea, this.billingId()),
			this.tenantTariffService.readAllTenantTarif(this.sessionService.effectiveArea, this.billingId()),
			this.tenantSettingService.readAllTenantSetting(this.sessionService.effectiveArea, this.billingId())
		)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: ([selections, payments, shares, tariffs, settings]) => {
					this.parameterConfigs[0].dataSource = new MatTableDataSource(shares);
					this.parameterConfigs[1].dataSource = new MatTableDataSource(payments);
					this.parameterConfigs[2].dataSource = new MatTableDataSource(selections);
					this.parameterConfigs[3].dataSource = new MatTableDataSource(tariffs);
					this.parameterConfigs[4].dataSource = new MatTableDataSource(settings);
				},
			});
	}

	private loadAvailableUserRoles() {
		this.userRoleService
			.readAllUserRole(this.sessionService.effectiveArea, this.billingId())
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (roles) => {
					this.availableUserRoles.set(roles);
				},
				error: () => {
					this.snackbar.error(this.translocoService.translate('errors.loadingBillingUserRoles'));
				},
			});
	}

	getUserRoleString(validUserRoleId: string) {
		const userRole = this.availableUserRoles().find((role) => role.exportId === validUserRoleId);
		if (!userRole) {
			return '';
		}
		let roleString = this.transloco.translate('enum.' + userRole.userRoleType);
		if (userRole.name?.trim()?.length && this.transloco.translate('enum.' + userRole.userRoleType) !== userRole.name?.trim()) {
			roleString += '(' + userRole.name + ')';
		}
		return roleString;
	}

	addParameter(parameter: string) {
		const matDialogRef = this.dialog.open(ParameterDialogComponent, {
			disableClose: true,
			width: '1000px',
			data: {
				billingId: this.billingId(),
				currentParameter: parameter,
				availableUserRoles: this.availableUserRoles(),
			},
		});
		matDialogRef.afterClosed().subscribe({
			next: (saved) => {
				if (saved) {
					this.fillTables();
				}
			},
		});
	}

	editParameter(parameter: string, row: TenantShare | TenantPayment | TenantSelection | TenantTarif | TenantSetting) {
		const matDialogRef = this.dialog.open(ParameterDialogComponent, {
			disableClose: true,
			width: '1000px',
			data: {
				billingId: this.billingId(),
				currentParameter: parameter,
				availableUserRoles: this.availableUserRoles(),
				parameter: row,
			},
		});
		matDialogRef.afterClosed().subscribe({
			next: (saved) => {
				if (saved) {
					this.fillTables();
				}
			},
		});
	}

	deleteParameter(parameter: string, row: TenantShare | TenantPayment | TenantSelection | TenantTarif | TenantSetting) {
		this.openConfirmationDialog()
			.pipe(
				filter((confirmed) => confirmed),
				switchMap(() => {
					switch (parameter) {
						case 'share':
							return this.tenantShareService.deleteTenantShare(this.sessionService.effectiveArea, this.billingId(), row.exportId);
						case 'payment':
							return this.tenantPaymentService.deleteTenantPayment(this.sessionService.effectiveArea, this.billingId(), row.exportId);
						case 'selection':
							return this.tenantSelectionService.deleteTenantSelection(this.sessionService.effectiveArea, this.billingId(), row.exportId);
						case 'tariff':
							return this.tenantTariffService.deleteTenantTarif(this.sessionService.effectiveArea, this.billingId(), row.exportId);
						case 'setting':
							return this.tenantSettingService.deleteTenantSetting(this.sessionService.effectiveArea, this.billingId(), row.exportId);
						default:
							return null;
					}
				})
			)
			.subscribe({
				next: () => {
					this.fillTables();
					this.snackbar.success(this.translocoService.translate('common.deleted'));
				},
				error: () => {
					this.snackbar.error(this.translocoService.translate('errors.delete'));
				},
			});
	}

	private openConfirmationDialog(): Observable<boolean> {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			disableClose: false,
			autoFocus: false,
			restoreFocus: false,
		});
		dialogRef.componentInstance.titleMessage = this.translocoService.translate('common.confirm');
		dialogRef.componentInstance.confirmMessage = this.translocoService.translate('common.confirmDelete');
		return dialogRef.afterClosed();
	}

	getTranslatedType(type: string, name: string) {
		let translatedType = '';
		if (!type) {
			return '';
		}

		if (name === 'share') {
			translatedType = this.transloco.translate('tenantShareType.' + type);
		} else if (name === 'payment') {
			translatedType = this.transloco.translate('tenantPaymentType.' + type);
		} else {
			translatedType = this.transloco.translate('billing.ordinaryAllocationType.' + type);
		}
		return translatedType;
	}
}
